/**
 * ProviderDomain - Provider Domain Service
 *
 * Handles all provider-related operations including registration, profile management,
 * authentication, and profile retrieval. Extends BaseDomain to leverage shared
 * infrastructure for API calls, retry logic, and error handling.
 *
 * Features:
 * - Provider account registration
 * - Authentication (login)
 * - Profile creation and updates
 * - Profile retrieval with retry logic
 * - Integration with StorageManager for token management
 * - Comprehensive error handling with ProviderApiError
 *
 * @module services/domains/ProviderDomain
 */

import { BaseDomain, BaseApiError, ApiClient, tokenManager, requestManager, getLoggerWithPrefix } from '../shared';
import { signIn } from 'supertokens-web-js/recipe/emailpassword/index.js';
import type {
  RegistrationApiRequest,
  ProfileUpdateApiRequest,
  RegistrationError,
  RegistrationErrorType
} from '@/types/registration';
import type {
  RegistrationResponse,
  LoginRequest,
  ProfileUpdateResponse,
  ProfileGetResponse
} from '@/types/provider';

/**
 * Provider-specific error class
 */
export class ProviderApiError extends BaseApiError {
  public readonly type: RegistrationErrorType;

  constructor(error: RegistrationError, statusCode?: number) {
    super(error.message, statusCode, error.fieldErrors, error.code);
    this.name = 'ProviderApiError';
    this.type = error.type;
  }

  /**
   * Create error from HTTP response
   */
  static fromHttpStatus(
    message: string,
    status?: number,
    fieldErrors?: Record<string, string>,
    code?: string
  ): ProviderApiError {
    // Map HTTP status codes to error types
    let errorType: RegistrationErrorType;
    switch (status) {
      case 400:
        errorType = 'validation_error';
        break;
      case 409:
        errorType = 'email_exists';
        break;
      case 422:
        errorType = 'validation_error';
        break;
      case 429:
        errorType = 'rate_limit_exceeded';
        break;
      case 500:
      case 502:
      case 503:
      case 504:
        errorType = 'server_error';
        break;
      default:
        errorType = 'network_error';
    }

    return new ProviderApiError({
      type: errorType,
      message,
      code,
      fieldErrors
    }, status);
  }
}

/**
 * Authentication response interface
 */
export interface AuthResponse {
  success: boolean;
  message: string;
}

/**
 * Provider domain service class
 */
export class ProviderDomain extends BaseDomain<ProviderApiError> {
  constructor() {
    const apiClient = new ApiClient<ProviderApiError>(
      ProviderApiError.fromHttpStatus,
      {
        // Use localhost:8000 in test environment to work with MSW
        baseUrl: import.meta.env.MODE === 'test' ? 'http://localhost:8000/api' : undefined
      }
    );

    super(apiClient, tokenManager, requestManager);
  }

  /**
   * Register a new provider account
   *
   * @param data - Provider registration data
   * @returns Promise resolving to registration response
   *
   * @example
   * ```typescript
   * try {
   *   const response = await providerDomain.register({
   *     email: 'doctor@example.com',
   *     password: 'SecurePass123',
   *     first_name: 'John',
   *     last_name: 'Doe',
   *     role: 'provider'
   *   });
   *   logger.log('Registration successful:', response.user_id);
   * } catch (error) {
   *   if (error instanceof ProviderApiError) {
   *     logger.error('Registration failed:', error.message);
   *   }
   * }
   * ```
   */
  async register(data: RegistrationApiRequest): Promise<RegistrationResponse> {
    return this.executeWithRetry(async () => {
      try {
        const result = await this.apiClient.post<RegistrationResponse>('/auth/signup', data);
        return result;
      } catch (error) {
        const httpError = error as { status?: number };
        throw this.createError(
          'register',
          error instanceof Error ? error.message : 'Registration failed',
          httpError.status
        );
      }
    });
  }

  /**
   * Login with email and password using SuperTokens
   *
   * @param data - Login credentials
   * @returns Promise resolving to authentication response
   *
   * @example
   * ```typescript
   * try {
   *   await providerDomain.login({
   *     email: 'doctor@example.com',
   *     password: 'SecurePass123'
   *   });
   *   logger.log('Login successful');
   * } catch (error) {
   *   if (error instanceof ProviderApiError) {
   *     logger.error('Login failed:', error.message);
   *   }
   * }
   * ```
   */
  async login(data: LoginRequest): Promise<AuthResponse> {
    const logger = getLoggerWithPrefix('ProviderDomain');
    return this.executeWithRetry(async () => {
      try {
        const response = await signIn({
          formFields: [{
            id: 'email',
            value: data.email.trim()
          }, {
            id: 'password',
            value: data.password
          }]
        });

        logger.debug('login response ', response);

        return {
          success: true,
          message: 'Login successful'
        };
      } catch (error) {
        throw this.createError(
          'login',
          error instanceof Error ? error.message : 'Login failed'
        );
      }
    });
  }

  /**
   * Update provider profile information
   *
   * @param data - Provider profile data
   * @returns Promise resolving to profile update response
   *
   * @example
   * ```typescript
   * try {
   *   const response = await providerDomain.updateProfile({
   *     provider_number: '1234567890',
   *     practice_name: 'Family Medical Center',
   *     phone: {
   *       country_code: '+61',
   *       iso_code: 'AU',
   *       number: '412345678'
   *     }
   *   });
   *   logger.log('Profile updated:', response.data.registration_status);
   * } catch (error) {
   *   if (error instanceof ProviderApiError) {
   *     logger.error('Profile update failed:', error.message);
   *   }
   * }
   * ```
   */
  async updateProfile(data: ProfileUpdateApiRequest): Promise<ProfileUpdateResponse> {
    return this.executeWithRetry(async () => {
      try {
        const result = await this.apiClient.put<ProfileUpdateResponse>('/users/me/provider-profile', data);
        return result;
      } catch (error) {
        const httpError = error as { status?: number };
        throw this.createError(
          'updateProfile',
          error instanceof Error ? error.message : 'Profile update failed',
          httpError.status
        );
      }
    });
  }

  /**
   * Get provider profile information
   *
   * @returns Promise resolving to profile data or 404 if not found
   *
   * @example
   * ```typescript
   * try {
   *   const profile = await providerDomain.getProfile();
   *   logger.log('Profile:', profile);
   * } catch (error) {
   *   if (error instanceof ProviderApiError && error.code === 'PROFILE_NOT_FOUND') {
   *     logger.log('Profile not found - user needs to complete profile');
   *   }
   * }
   * ```
   */
  async getProfile(): Promise<ProfileGetResponse> {
    return this.executeWithDeduplication('provider-profile', async () => {
      try {
        const result = await this.apiClient.get<ProfileGetResponse>('/users/me/provider-profile');
        return result;
      } catch (error) {
        // Handle timeout errors from API client
        if (error instanceof Error && error.name === 'AbortError') {
          throw this.createError(
            'getProfile',
            'Request timeout. Please check your connection and try again.',
            undefined,
            undefined,
            'TIMEOUT'
          );
        }

        // Handle other API client errors that might have error codes
        if (error && typeof error === 'object' && 'code' in error) {
          const apiError = error as {
            message?: string;
            status?: number;
            fieldErrors?: Record<string, string>;
            code?: string;
          };
          throw this.createError(
            'getProfile',
            apiError.message || 'An unexpected error occurred. Please try again.',
            apiError.status,
            apiError.fieldErrors,
            apiError.code
          );
        }

        const httpError = error as { status?: number };
        throw this.createError(
          'getProfile',
          error instanceof Error ? error.message : 'Failed to fetch provider profile',
          httpError.status
        );
      }
    });
  }

  /**
   * Verify email address
   *
   * @param token - Email verification token
   * @returns Promise resolving to verification response
   */
  async verifyEmail(token: string): Promise<{ success: boolean; message: string }> {
    return this.executeWithRetry(async () => {
      try {
        const result = await this.apiClient.post<{ success: boolean; message: string }>(`/auth/verify-email?token=${token}`);
        return result;
      } catch (error) {
        const httpError = error as { status?: number };
        throw this.createError(
          'verifyEmail',
          error instanceof Error ? error.message : 'Email verification failed',
          httpError.status
        );
      }
    });
  }

  /**
   * Resend email verification
   *
   * @param email - Email address to resend verification to
   * @returns Promise resolving to resend response
   */
  async resendVerification(email: string): Promise<{ success: boolean; message: string }> {
    return this.executeWithRetry(async () => {
      try {
        const result = await this.apiClient.post<{ success: boolean; message: string }>('/auth/resend-verification', { email });
        return result;
      } catch (error) {
        const httpError = error as { status?: number };
        throw this.createError(
          'resendVerification',
          error instanceof Error ? error.message : 'Failed to resend verification email',
          httpError.status
        );
      }
    });
  }

  /**
   * Check if API is available
   *
   * @returns Promise resolving to health check response
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    return this.executeWithRetry(async () => {
      try {
        const result = await this.apiClient.get<{ status: string; timestamp: string }>('/health');
        return result;
      } catch (error) {
        const httpError = error as { status?: number };
        throw this.createError(
          'healthCheck',
          error instanceof Error ? error.message : 'Health check failed',
          httpError.status
        );
      }
    });
  }

  /**
   * Get domain name for error messages
   *
   * @protected
   * @returns Domain name
   */
  protected getDomainName(): string {
    return 'Provider';
  }

  /**
   * Create domain-specific error instance
   *
   * @protected
   * @param operation - The operation that failed
   * @param message - Error message
   * @param status - HTTP status code
   * @param fieldErrors - Field-specific validation errors
   * @param code - Error code for programmatic handling
   * @returns ProviderApiError instance
   */
  protected createDomainError(
    operation: string,
    message: string,
    status?: number,
    fieldErrors?: Record<string, string>,
    code?: string
  ): ProviderApiError {
    const formattedMessage = `${operation}: ${message}`;

    // Map HTTP status codes to error types for domain error
    let errorType: RegistrationErrorType;
    switch (status) {
      case 400:
        errorType = 'validation_error';
        break;
      case 409:
        errorType = 'email_exists';
        break;
      case 422:
        errorType = 'validation_error';
        break;
      case 429:
        errorType = 'rate_limit_exceeded';
        break;
      case 500:
      case 502:
      case 503:
      case 504:
        errorType = 'server_error';
        break;
      default:
        errorType = 'network_error';
    }

    return new ProviderApiError({
      type: errorType,
      message: formattedMessage,
      code,
      fieldErrors
    }, status);
  }
}

/**
 * Default ProviderDomain instance
 */
export const providerDomain = new ProviderDomain();